import React, { useEffect, useState, useMemo } from "react";
import GTranslateOutlinedIcon from "@mui/icons-material/GTranslateOutlined";
import { useSelector, useDispatch } from "react-redux";
import Button from "@mui/material/Button";
import { makeStyles } from "@mui/styles";
import { useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import { getLastUpdateTime } from "../../apis";
import { handleWaitlistOpenStatus } from "../../store/slices/waitlist";
import { showAlert } from "../../store/slices/feedback";
import { useGlobalState } from "../../hooks/useGlobalState";

const GetInLine = styled(Button)({
  //width: "377px",
  color: "#000",
  height: "74px",
  borderRadius: "8.4px",
  background: "#f9d023",
  fontSize: "28px",
  marginTop: 20,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#f9d023",
  },
  "&:active": {
    backgroundColor: "#f9d023",
  },
  "&:disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.12)",
  },
  // '&:focus': {
  //   boxShadow: '0 0 0 0.2rem rgba(0,123,255,.5)',
  // },
});

const useStyles = makeStyles({
  root: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    color: "#4a4f54",
  },
  language: {
    position: "absolute",
    right: 24,
    top: 42,
  },
  marginTop: {
    marginTop: 67,
  },
  partyCount: {
    height: "43px",
    fontSize: "36px",
    fontWeight: 500,
  },
  getInLine: {
    marginTop: "30px",
    fontSize: 28,
  },
  tableRoot: {
    width: 680,
    marginTop: 30,
  },
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
  },
  tableRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    height: 70,
    alignItems: "center",
    padding: "0 50px",
    fontSize: 28,
  },
  tableHeaderRow: {
    height: 80,
  },
  tableTh: {
    textAlign: "center",
  },
  gray: {
    backgroundColor: "#ededed",
  },
  partySize: {
    width: 120,
    textAlign: "center",
  },
  buttonMargin: {
    marginTop: "30px",
  },
  buttonRoot: {
    width: "377px",
    height: "74px",
    borderRadius: "8.4px",
    background: "#f9d023",
    fontSize: 28,
  },
  buttonLabel: {},
  itemName: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    width: 290,
    overflow: "hidden",
  },
  waitingNumber: {
    width: 110,
    textAlign: "center",
  },
  tableContent: {
    height: "calc(100vh - 358px)",
    overflowY: "auto",
  },
});

export default ({ onLoad }) => {
  const [language, setLanguage] = useGlobalState("language");
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { merchantId } = useParams();
  const waitlist = useSelector((state) => state.waitlist.list);
  const merchant = useSelector((state) => state.merchant);
  const disabled = useSelector((state) => state.waitlist.disabled);
  const [heartbeat, setHeartbeat] = useState(Date.now());
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const partyCount = waitlist.length;
  const nextPage = window.location.pathname.replace(/\/[^\/]*$/, "/partySize");

  useEffect(() => {
    const interval = setInterval(async () => {
      const now = Date.now();
      const refreshInterval = 1000 * 5;
      if (now > heartbeat + refreshInterval) {
        try {
          if (merchant) {
            const { businessHours, isOpen, timeZone } = merchant;
            handleWaitlistOpenStatus(businessHours, isOpen, timeZone);
          }
          let fetchLastUpdateTime = await getLastUpdateTime(merchantId);
          const newLastUpdate = fetchLastUpdateTime.data.lastUpdateTime;
          if (lastUpdateTime !== newLastUpdate) {
            setLastUpdateTime(newLastUpdate);
            onLoad();
          }
        } catch (err) {
          console.log(err);
          dispatch(showAlert("Failed to load data. "));
        }
        setHeartbeat(now);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  });

  const handleChangeLanguage = () => {
    const language = localStorage.getItem("language");
    const setLang = language.includes("zh") ? "en" : "zh-CN";
    setLanguage(setLang);
  };

  const isShowCn = useMemo(() => {
    return language.includes("zh");
  }, [language]);

  return (
    <div className={classes.root}>
      <GTranslateOutlinedIcon
        sx={{
          fontSize: "32px",
        }}
        className={classes.language}
        onClick={handleChangeLanguage}
      />
      <div className={`${classes.partyCount} ${classes.marginTop}`}>
        <span>Current wait: {partyCount} parties</span>
      </div>
      {isShowCn && (
        <div className={classes.partyCount}>
          <span>目前等待: {partyCount} 桌</span>
        </div>
      )}

      <GetInLine
        variant="contained"
        onClick={() => navigate(nextPage)}
        disabled={disabled}
      >
        <span> Join the Waitlist </span>
        {isShowCn && <span>(加入排队)</span>}
      </GetInLine>
      <WaitlistTable data={waitlist} isShowCn={isShowCn} />
    </div>
  );
};

function WaitlistTable({ data, isShowCn }) {
  const classes = useStyles();
  return (
    <div className={classes.tableRoot}>
      <div className={`${classes.tableRow} ${classes.tableHeaderRow}`}>
        <div className={classes.itemName}>
          <span>Name</span>{isShowCn && <><br /><span>(姓名)</span></>}
        </div>
        <div className={classes.waitingNumber}>
          <span>Number</span>{isShowCn && <><br /><span>(号码)</span></>}
        </div>
        <div className={classes.partySize}>
          <span>Party size</span>{isShowCn && <><br /><span>(人数)</span></>}
        </div>
      </div>
      <div className={classes.tableContent}>
        {data.map((item, i) => (
          <div
            key={item._id}
            className={`${classes.tableRow} ${i % 2 === 0 ? classes.gray : ""}`}
          >
            <div className={classes.itemName}>{item.name}</div>
            <div className={classes.waitingNumber}>{item.waitingNumber}</div>
            <div className={classes.partySize}>{item.partySize}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
